import type { CrmDataProvider } from "../providers/types";
import type {
  ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type {
  AssigneeRule,
  Contact,
  SequenceDef,
  SequenceStepDef,
  WorkflowAction,
  WorkflowDef,
} from "../types";

/**
 * The CRM-write tools Ask AI can call (decision #51, workflow authoring
 * decision #59). Provider-neutral shape - byoai.ts adapts this to
 * Anthropic's `input_schema` and OpenAI's nested `function.parameters` at
 * request time (both accept plain JSON Schema, so `parameters` here IS
 * that schema, unmodified). Read-only by default in spirit (architecture
 * invariant #4): these are the only writes offered. Most are a plain
 * dataProvider.create() - the same path a human's "New X" form uses;
 * create_workflow instead goes through the same
 * dataProvider.updateConfiguration() every builder in Marketing uses
 * (decision #52), since a workflow lives in tenant config, not its own
 * table.
 */
export interface JsonSchemaProperty {
  type: "string" | "number" | "boolean" | "object" | "array";
  description?: string;
  enum?: string[];
  items?: JsonSchemaProperty;
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
}

export interface AiToolDef {
  name: string;
  description: string;
  parameters: {
    type: "object";
    properties: Record<string, JsonSchemaProperty>;
    required: string[];
  };
}

export const AI_TOOLS: AiToolDef[] = [
  {
    name: "create_contact",
    description: "Create a new contact (a person) in the CRM.",
    parameters: {
      type: "object",
      properties: {
        first_name: { type: "string" },
        last_name: { type: "string" },
        email: { type: "string", description: "Optional work email address." },
        title: { type: "string", description: "Optional job title." },
        company_name: {
          type: "string",
          description: "Optional - name of an EXISTING company to link this contact to.",
        },
      },
      required: ["first_name", "last_name"],
    },
  },
  {
    name: "create_company",
    description: "Create a new company in the CRM.",
    parameters: {
      type: "object",
      properties: {
        name: { type: "string" },
        website: { type: "string", description: "Optional." },
        sector: { type: "string", description: "Optional industry/sector." },
      },
      required: ["name"],
    },
  },
  {
    name: "create_deal",
    description: "Create a new deal (sales opportunity) in the CRM, in the first pipeline stage.",
    parameters: {
      type: "object",
      properties: {
        name: { type: "string" },
        company_name: {
          type: "string",
          description: "Optional - name of an EXISTING company this deal is with.",
        },
        amount: { type: "number", description: "Optional deal value." },
        expected_closing_date: {
          type: "string",
          description: "Optional ISO 8601 date, e.g. 2026-09-20. Defaults to today.",
        },
      },
      required: ["name"],
    },
  },
  {
    name: "create_task",
    description: "Create a follow-up task linked to an EXISTING contact.",
    parameters: {
      type: "object",
      properties: {
        contact_name: { type: "string", description: "Full name of the existing contact this task is for." },
        text: { type: "string", description: "What the task is." },
        type: { type: "string", description: "Optional task type, e.g. Call, Email, Meeting." },
        due_date: { type: "string", description: "Optional ISO 8601 date, e.g. 2026-09-20." },
      },
      required: ["contact_name", "text"],
    },
  },
  {
    name: "create_workflow",
    description:
      "Create a marketing automation workflow: a trigger plus a linear sequence of actions that run when it fires. Linear only - no if/then branching (open Marketing → Workflows afterward to add branches). Created disabled so a person reviews it before it starts acting on real contacts.",
    parameters: {
      type: "object",
      properties: {
        name: { type: "string", description: "Workflow name." },
        trigger_type: {
          type: "string",
          enum: [
            "contact_created",
            "form_submitted",
            "deal_created",
            "deal_stage_changed",
            "email_replied",
            "score_crossed",
            "tag_added",
            "no_activity",
          ],
          description: "What starts the workflow.",
        },
        trigger_value: {
          type: "string",
          description:
            "Only for deal_stage_changed (an existing pipeline stage's name), score_crossed (a number threshold), tag_added (a tag name), or no_activity (number of idle days). Omit for other trigger types.",
        },
        actions: {
          type: "array",
          description: "One or more actions, run in order, each time the trigger fires.",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: [
                  "adjust_score",
                  "set_owner",
                  "add_tag",
                  "enroll_sequence",
                  "create_task",
                  "send_email",
                  "notify_user",
                ],
                description: "The action to run.",
              },
              value: {
                type: "string",
                description:
                  'adjust_score: a signed number, e.g. "10" or "-5". add_tag: a tag name (created if new). enroll_sequence: an EXISTING sequence\'s name. send_email: an EXISTING email template\'s name. create_task: the task title (supports {{first_name}} {{last_name}} {{company}} tokens).',
              },
              assignee: {
                type: "string",
                description:
                  'For set_owner/create_task/notify_user: an existing teammate\'s full name, or "owner" for the contact\'s current owner (not valid for set_owner - defaults to round-robin there instead). Defaults to owner.',
              },
              message: { type: "string", description: "For notify_user: the note to include." },
              task_type: { type: "string", description: "For create_task, e.g. Call, Email, Meeting." },
              due_in_days: {
                type: "number",
                description: "For create_task: days after the run it's due. Default 0.",
              },
              subject_override: {
                type: "string",
                description: "For send_email: override the template's own subject line.",
              },
            },
            required: ["type"],
          },
        },
      },
      required: ["name", "trigger_type", "actions"],
    },
  },
  {
    name: "create_sequence",
    description:
      "Create a multi-step email/task cadence (a sequence): email, wait, and task steps that run in order for each enrolled contact, stopping early if they reply (unless unenroll_on_reply is turned off). A sequence does nothing on its own - enroll contacts into it via a workflow's enroll_sequence action (create_workflow), or manually from Marketing → Sequences.",
    parameters: {
      type: "object",
      properties: {
        name: { type: "string", description: "Sequence name." },
        unenroll_on_reply: {
          type: "boolean",
          description: "Stop the sequence for a contact once they reply. Defaults to true.",
        },
        business_hours_only: {
          type: "boolean",
          description: "Only send/advance on weekdays during business hours. Defaults to false.",
        },
        steps: {
          type: "array",
          description: "One or more steps, run in order.",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: ["email", "wait", "task"],
                description: "The step to run.",
              },
              template_name: {
                type: "string",
                description: "For email steps: an EXISTING email template's name.",
              },
              days: {
                type: "number",
                description: "For wait steps: how many days to wait before the next step. Default 1.",
              },
              task_title: {
                type: "string",
                description:
                  "For task steps: what the task is (supports {{first_name}} {{last_name}} {{company}} tokens).",
              },
              assignee: {
                type: "string",
                description:
                  'For task steps: an existing teammate\'s full name, or "owner" for the contact\'s current owner. Defaults to owner.',
              },
              task_type: { type: "string", description: "For task steps, e.g. Call, Email, Meeting." },
            },
            required: ["type"],
          },
        },
      },
      required: ["name", "steps"],
    },
  },
];

export interface AiToolContext {
  dataProvider: CrmDataProvider;
  /** Ask AI's own already-loaded CRM snapshot - resolves create_task's contact_name without a second fetch. */
  contacts: Contact[];
  config: ConfigurationContextValue;
  /** The same local-cache setter every Marketing builder uses (decision #52) - create_workflow updates it immediately, then persists for real below. */
  updateConfig: (next: ConfigurationContextValue) => void;
}

async function findCompanyIdByName(
  dataProvider: CrmDataProvider,
  name: string,
): Promise<number | null> {
  const { data } = await dataProvider.getList("companies", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "name", order: "ASC" },
    filter: { "name@ilike": `%${name}%` },
  });
  return data[0] ? Number(data[0].id) : null;
}

function findContactByName(contacts: Contact[], name: string): Contact | null {
  const needle = name.trim().toLowerCase();
  const fullName = (c: Contact) => `${c.first_name} ${c.last_name}`.toLowerCase();
  return (
    contacts.find((c) => fullName(c) === needle) ??
    contacts.find((c) => fullName(c).includes(needle)) ??
    null
  );
}

const asString = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const VALID_TRIGGER_TYPES = new Set<WorkflowDef["trigger"]["type"]>([
  "contact_created",
  "form_submitted",
  "deal_created",
  "deal_stage_changed",
  "email_replied",
  "score_crossed",
  "tag_added",
  "no_activity",
]);

const VALID_ACTION_TYPES = new Set<WorkflowAction["type"]>([
  "adjust_score",
  "set_owner",
  "add_tag",
  "enroll_sequence",
  "create_task",
  "send_email",
  "notify_user",
]);

const VALID_SEQUENCE_STEP_TYPES = new Set<SequenceStepDef["type"]>(["email", "wait", "task"]);

let workflowUid = Date.now() % 100000;
const nextWorkflowUid = () => String(++workflowUid);

/**
 * Resolves an assignee name to an AssigneeRule the same way the visual
 * builder's picker does. "owner" (or omitted) means the contact's current
 * owner, EXCEPT for set_owner - the builder disallows that mode there
 * (you can't set the owner to "the current owner"), so it falls back to
 * round-robin instead.
 */
async function resolveAssignee(
  dataProvider: CrmDataProvider,
  assignee: string,
  actionType: WorkflowAction["type"],
): Promise<AssigneeRule | string> {
  const wantsOwner = !assignee || assignee.toLowerCase() === "owner";
  if (wantsOwner) {
    return actionType === "set_owner" ? { mode: "round_robin" } : { mode: "owner" };
  }
  const { data: sales } = await dataProvider.getList("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: {},
  });
  const needle = assignee.toLowerCase();
  const match = sales.find(
    (s) => `${s.first_name} ${s.last_name}`.toLowerCase().includes(needle),
  );
  if (!match) {
    const names = sales.map((s) => `${s.first_name} ${s.last_name}`).join(", ");
    return `Couldn't find a teammate named "${assignee}". Team members: ${names || "none set up yet"}.`;
  }
  return { mode: "user", userId: String(match.id) };
}

export async function executeAiTool(
  name: string,
  input: Record<string, unknown>,
  ctx: AiToolContext,
): Promise<string> {
  const { dataProvider, contacts, config, updateConfig } = ctx;
  try {
    switch (name) {
      case "create_contact": {
        const first_name = asString(input.first_name);
        const last_name = asString(input.last_name);
        if (!first_name || !last_name) return "first_name and last_name are required.";
        const email = asString(input.email);
        const companyName = asString(input.company_name);
        const company_id = companyName ? await findCompanyIdByName(dataProvider, companyName) : null;

        const { data } = await dataProvider.create("contacts", {
          data: {
            first_name,
            last_name,
            title: asString(input.title),
            email_jsonb: email ? [{ email, type: "Work" }] : [],
            phone_jsonb: [],
            tags: [],
            first_seen: new Date().toISOString(),
            last_seen: new Date().toISOString(),
            company_id,
          },
        });
        const companyNote =
          companyName && !company_id
            ? ` (no company named "${companyName}" was found, so it was left unlinked)`
            : "";
        return `Created contact "${first_name} ${last_name}" (id ${data.id})${companyNote}.`;
      }

      case "create_company": {
        const companyName = asString(input.name);
        if (!companyName) return "name is required.";
        const { data } = await dataProvider.create("companies", {
          data: { name: companyName, website: asString(input.website), sector: asString(input.sector) },
        });
        return `Created company "${companyName}" (id ${data.id}).`;
      }

      case "create_deal": {
        const dealName = asString(input.name);
        if (!dealName) return "name is required.";
        const companyName = asString(input.company_name);
        const company_id = companyName ? await findCompanyIdByName(dataProvider, companyName) : null;
        const stage = config.dealStages?.[0]?.value ?? "opportunity";
        const amount = typeof input.amount === "number" ? input.amount : 0;
        // Same default the manual "New Deal" form uses - expected_closing_date
        // is a plain `date` column with no DB-level default, so an omitted
        // value here would otherwise land as null and crash DealShow's render.
        const expectedClosingDate =
          asString(input.expected_closing_date) || new Date().toISOString().split("T")[0];

        const { data } = await dataProvider.create("deals", {
          data: {
            name: dealName,
            company_id,
            amount,
            stage,
            contact_ids: [],
            expected_closing_date: expectedClosingDate,
          },
        });
        const companyNote =
          companyName && !company_id
            ? ` (no company named "${companyName}" was found, so it was left unlinked)`
            : "";
        return `Created deal "${dealName}" (id ${data.id}) in stage "${stage}"${companyNote}.`;
      }

      case "create_task": {
        const contactName = asString(input.contact_name);
        const text = asString(input.text);
        if (!contactName || !text) return "contact_name and text are required.";
        const contact = findContactByName(contacts, contactName);
        if (!contact) {
          return `Couldn't find a contact named "${contactName}" - create the contact first, or check the spelling.`;
        }
        const dueDate = asString(input.due_date);
        const { data } = await dataProvider.create("tasks", {
          data: {
            contact_id: contact.id,
            text,
            type: asString(input.type) || "none",
            due_date: dueDate || new Date().toISOString(),
          },
        });
        return `Created task "${text}" for ${contact.first_name} ${contact.last_name} (id ${data.id}).`;
      }

      case "create_workflow": {
        const workflowName = asString(input.name);
        if (!workflowName) return "name is required.";
        const triggerType = asString(input.trigger_type) as WorkflowDef["trigger"]["type"];
        if (!VALID_TRIGGER_TYPES.has(triggerType)) {
          return `trigger_type must be one of: ${[...VALID_TRIGGER_TYPES].join(", ")}.`;
        }
        const rawActions = Array.isArray(input.actions) ? input.actions : [];
        if (rawActions.length === 0) return "actions must have at least one entry.";

        let triggerValue = asString(input.trigger_value);
        if (triggerType === "deal_stage_changed") {
          const stage = (config.dealStages ?? []).find(
            (s) => s.value === triggerValue || s.label.toLowerCase() === triggerValue.toLowerCase(),
          );
          if (!stage) {
            const stages = (config.dealStages ?? []).map((s) => s.label).join(", ");
            return `trigger_value must be an existing pipeline stage: ${stages || "none configured"}.`;
          }
          triggerValue = stage.value;
        }

        const actions: WorkflowAction[] = [];
        for (const [i, raw] of rawActions.entries()) {
          const a = (raw ?? {}) as Record<string, unknown>;
          const actionType = asString(a.type) as WorkflowAction["type"];
          if (!VALID_ACTION_TYPES.has(actionType)) {
            return `actions[${i}].type must be one of: ${[...VALID_ACTION_TYPES].join(", ")}.`;
          }
          const value = asString(a.value);
          const config_: Record<string, unknown> = {};

          if (actionType === "enroll_sequence") {
            const seq = (config.sequences ?? []).find(
              (s) => s.name.toLowerCase() === value.toLowerCase(),
            );
            if (!seq) {
              const names = (config.sequences ?? []).map((s) => s.name).join(", ");
              return `actions[${i}]: no sequence named "${value}". Existing sequences: ${names || "none yet"}.`;
            }
          }
          if (actionType === "send_email") {
            const tpl = (config.emailTemplates ?? []).find(
              (t) => t.name.toLowerCase() === value.toLowerCase(),
            );
            if (!tpl) {
              const names = (config.emailTemplates ?? []).map((t) => t.name).join(", ");
              return `actions[${i}]: no email template named "${value}". Existing templates: ${names || "none yet"}.`;
            }
            if (asString(a.subject_override)) config_.subjectOverride = asString(a.subject_override);
          }
          if (actionType === "adjust_score" && !/^-?\d+$/.test(value)) {
            return `actions[${i}]: value must be a whole number (e.g. "10" or "-5") for adjust_score.`;
          }

          if (actionType === "set_owner" || actionType === "create_task" || actionType === "notify_user") {
            const assignee = await resolveAssignee(dataProvider, asString(a.assignee), actionType);
            if (typeof assignee === "string") return `actions[${i}]: ${assignee}`;
            config_.assignee = assignee;
          }
          if (actionType === "create_task") {
            config_.dueInDays = typeof a.due_in_days === "number" ? Math.max(0, a.due_in_days) : 0;
            if (asString(a.task_type)) config_.taskType = asString(a.task_type);
          }
          if (actionType === "notify_user" && asString(a.message)) {
            config_.message = asString(a.message);
          }

          // enroll_sequence/send_email store the resolved NAME's real id
          // as params.value, exactly like the visual builder - re-resolve
          // rather than reuse the lookups above so a rename mid-loop (a
          // duplicate sequence/template name) can't produce a mismatch.
          const resolvedValue =
            actionType === "enroll_sequence"
              ? (config.sequences ?? []).find((s) => s.name.toLowerCase() === value.toLowerCase())!.id
              : actionType === "send_email"
                ? (config.emailTemplates ?? []).find((t) => t.name.toLowerCase() === value.toLowerCase())!.id
                : value;

          actions.push({
            id: nextWorkflowUid(),
            type: actionType,
            params: { value: resolvedValue },
            config: config_,
          });
        }

        const workflow: WorkflowDef = {
          id: `wf-${nextWorkflowUid()}`,
          name: workflowName,
          enabled: false,
          trigger: { type: triggerType, params: triggerValue ? { value: triggerValue } : {} },
          actions,
        };
        const nextConfig = { ...config, workflows: [...(config.workflows ?? []), workflow] };
        updateConfig(nextConfig);
        try {
          await dataProvider.updateConfiguration(nextConfig);
        } catch {
          return `Created "${workflowName}" locally, but couldn't save it - you may need admin access to update workflows.`;
        }
        return `Created workflow "${workflowName}" with ${actions.length} action${actions.length === 1 ? "" : "s"} - it's disabled for now. Review and turn it on in Marketing → Workflows.`;
      }

      case "create_sequence": {
        const sequenceName = asString(input.name);
        if (!sequenceName) return "name is required.";
        const rawSteps = Array.isArray(input.steps) ? input.steps : [];
        if (rawSteps.length === 0) return "steps must have at least one entry.";

        const steps: SequenceStepDef[] = [];
        for (const [i, raw] of rawSteps.entries()) {
          const s = (raw ?? {}) as Record<string, unknown>;
          const stepType = asString(s.type) as SequenceStepDef["type"];
          if (!VALID_SEQUENCE_STEP_TYPES.has(stepType)) {
            return `steps[${i}].type must be one of: ${[...VALID_SEQUENCE_STEP_TYPES].join(", ")}.`;
          }

          if (stepType === "email") {
            const templateName = asString(s.template_name);
            const tpl = (config.emailTemplates ?? []).find(
              (t) => t.name.toLowerCase() === templateName.toLowerCase(),
            );
            if (!tpl) {
              const names = (config.emailTemplates ?? []).map((t) => t.name).join(", ");
              return `steps[${i}]: no email template named "${templateName}". Existing templates: ${names || "none yet"}.`;
            }
            steps.push({ id: nextWorkflowUid(), type: "email", templateId: tpl.id });
          } else if (stepType === "wait") {
            const days = typeof s.days === "number" ? Math.max(1, s.days) : 1;
            steps.push({ id: nextWorkflowUid(), type: "wait", days });
          } else {
            const assignee = await resolveAssignee(dataProvider, asString(s.assignee), "create_task");
            if (typeof assignee === "string") return `steps[${i}]: ${assignee}`;
            steps.push({
              id: nextWorkflowUid(),
              type: "task",
              taskTitle: asString(s.task_title) || "Follow up",
              assignee,
              taskType: asString(s.task_type) || undefined,
            });
          }
        }

        const sequence: SequenceDef = {
          id: `seq-${nextWorkflowUid()}`,
          name: sequenceName,
          unenrollOnReply: input.unenroll_on_reply !== false,
          businessHoursOnly: input.business_hours_only === true,
          steps,
        };
        const nextConfig = { ...config, sequences: [...(config.sequences ?? []), sequence] };
        updateConfig(nextConfig);
        try {
          await dataProvider.updateConfiguration(nextConfig);
        } catch {
          return `Created "${sequenceName}" locally, but couldn't save it - you may need admin access to update sequences.`;
        }
        return `Created sequence "${sequenceName}" with ${steps.length} step${steps.length === 1 ? "" : "s"}. Enroll contacts via a workflow's enroll_sequence action, or from Marketing → Sequences.`;
      }

      default:
        return `Unknown tool "${name}".`;
    }
  } catch (e) {
    return `Failed to run ${name}: ${e instanceof Error ? e.message : "unknown error"}`;
  }
}
